import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

const stages = [
  {
    label: "Before",
    title: "The first email",
    body: (
      <>
        Once you submit an appointment request, Kathleen will reply personally
        within 1–2 business days. She&rsquo;ll ask a few short questions about
        your goals, any recent medical reports, and your availability across
        time zones. You&rsquo;ll receive a short intake form, a clear note about
        fees, and a secure Zoom for Healthcare link for your first session.
      </>
    ),
  },
  {
    label: "Session 1",
    title: "Your first 50 minutes",
    body: (
      <>
        The first session is part conversation, part assessment. We spend
        time understanding what brought you here — and what you want to be
        different. Depending on your concern, Kathleen may carry out a
        clinical voice screen, a cognitive-communication screen, an oral motor
        examination, or a bedside swallow screen.
        <br />
        <br />
        By the end, you&rsquo;ll have an honest picture of what therapy can
        realistically achieve, a draft plan with measurable goals, and a clear
        recommendation on session frequency.
      </>
    ),
  },
  {
    label: "Therapy",
    title: "What weekly sessions look like",
    body: (
      <>
        Most adult sessions run 45–60 minutes, with structured home practice
        between sessions. Plans vary by need:
        <ul className="list-disc pl-6 mt-3 space-y-1.5">
          <li>
            <strong>SPEAK OUT!®</strong> — 12 individual sessions (typically
            3×/week for 4 weeks).
          </li>
          <li>
            <strong>Aphasia / post-stroke language</strong> — typically 8–12
            weeks, reassessed every 4 weeks.
          </li>
          <li>
            <strong>Cognitive-communication therapy</strong> — 8–12 weeks with
            functional, life-anchored goals.
          </li>
          <li>
            <strong>Dysphagia / swallow management</strong> — reassessed every
            4–6 weeks; instrumental referral as clinically indicated.
          </li>
          <li>
            <strong>Voice therapy</strong> — typically 6–10 weeks, with
            maintenance sessions as needed.
          </li>
        </ul>
      </>
    ),
  },
  {
    label: "Progress",
    title: "How we track real change",
    body: (
      <>
        Honesty is non-negotiable. At every 4–6 week mark we reassess —
        re-running the relevant measures, comparing them to baseline, and
        deciding together whether to continue, change direction, or graduate
        from therapy. You always know where you stand.
      </>
    ),
  },
  {
    label: "After",
    title: "Maintenance &amp; the long view",
    body: (
      <>
        Most adult conditions benefit from periodic booster sessions —
        especially Parkinson&rsquo;s voice work and post-stroke recovery.
        We&rsquo;ll agree a check-in cadence that respects your time, your
        budget, and the realistic biology of long-term carry-over. You leave
        with the tools to keep practising on your own.
      </>
    ),
  },
];

const tips = [
  {
    title: "A quiet, private space",
    body:
      "A door that closes, minimal background noise, and a chair where you can sit comfortably upright for an hour.",
  },
  {
    title: "Headphones with a microphone",
    body:
      "Even budget headphones dramatically improve clarity — especially for voice and articulation work.",
  },
  {
    title: "Good lighting on your face",
    body:
      "Window light is ideal. Kathleen needs to see your mouth, jaw and posture clearly.",
  },
  {
    title: "A glass of water",
    body:
      "Voice and swallow sessions involve a lot of vocal effort — hydration matters.",
  },
];

export default function WhatToExpect() {
  usePageMeta({
    title: "What to Expect — Your First Session",
    description:
      "From the first email to your last session — exactly what working with Kathleen McGrath, M.S. CCC-SLP looks like. Telehealth setup, session flow, progress tracking and long-term maintenance.",
    path: "/what-to-expect",
  });
  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="wte-back-home" className="flex items-center">
            <img
              src="/logo.jpg"
              alt="Neurological Communication Partners"
              className="h-14 w-auto rounded-xl ring-1 ring-black/5"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#5C6661] hover:text-[#2D332F] transition"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
          What to expect
        </p>
        <h1
          data-testid="wte-title"
          className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]"
        >
          From the first email
          <br />
          <em className="text-[#739072]">to your last session.</em>
        </h1>
        <p className="mt-6 text-[#5C6661] text-lg leading-relaxed">
          Coming into speech &amp; language therapy as an adult can feel
          uncertain — especially after a neurological event. Here&rsquo;s
          exactly what working with Kathleen looks like, step by step.
        </p>

        <div className="mt-14 space-y-12">
          {stages.map((s, i) => (
            <section
              key={s.label}
              data-testid={`wte-stage-${i}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-t border-black/10 pt-8"
            >
              <div className="md:col-span-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#739072] mb-2">
                  Stage {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F]">
                  {s.title}
                </h2>
                <p className="text-xs uppercase tracking-[0.18em] text-[#5C6661] mt-2">
                  {s.label}
                </p>
              </div>
              <div className="md:col-span-8 text-[#2D332F]/85 text-base md:text-lg leading-relaxed">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-20 border-t border-black/10 pt-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-4">
            Setting up for telehealth
          </p>
          <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] mb-8">
            Four things that make remote sessions work brilliantly.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {tips.map((t) => (
              <div
                key={t.title}
                data-testid={`wte-tip-${t.title.toLowerCase().replace(/\s|&/g, "-")}`}
                className="rounded-2xl border border-black/5 bg-white p-5"
              >
                <p className="font-medium text-[#2D332F] mb-1.5">{t.title}</p>
                <p className="text-sm text-[#5C6661] leading-relaxed">
                  {t.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 rounded-3xl border border-black/5 bg-[#F3F1E7] p-8 md:p-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-3">
            Ready when you are
          </p>
          <p className="font-serif text-2xl md:text-3xl font-light text-[#2D332F] leading-snug mb-5 max-w-2xl">
            The first email is the hardest. The rest is just one good
            conversation after another.
          </p>
          <Link
            to="/#contact"
            data-testid="wte-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm hover:bg-[#739072] transition"
          >
            Request a consult
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </main>
    </div>
  );
}
